import { useRef, useState, useCallback } from "react";
import { Search, MapPin, Clock, Phone, Globe, ImageOff, HelpCircle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal } from "@/components/Reveal";

export const VisibilitySlider = () => {
  const { t } = useLang();
  const containerRef = useRef(null);
  const [pct, setPct] = useState(50);
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const rect = containerRef.current.getBoundingClientRect();
    const p = Math.min(96, Math.max(4, ((clientX - rect.left) / rect.width) * 100));
    setPct(p);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    containerRef.current.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => dragging.current && updateFromClientX(e.clientX);
  const onPointerUp = () => (dragging.current = false);
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPct((p) => Math.max(4, p - 5));
    if (e.key === "ArrowRight") setPct((p) => Math.min(96, p + 5));
  };

  return (
    <section className="bg-[#191713] py-20 lg:py-32" id="reveal" data-testid="visibility-reveal-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="eyebrow-label mb-6">{t.reveal.eyebrow}</p>
          <h2 className="font-serif text-[#F7F3EC] text-3xl sm:text-4xl lg:text-5xl max-w-2xl">{t.reveal.title}</h2>
          <p className="mt-4 text-[#9A9184] max-w-xl">{t.reveal.sub}</p>
        </Reveal>

        <Reveal delay={0.15} className="mt-12">
          <div
            ref={containerRef}
            className="relative select-none touch-none gold-keyline overflow-hidden cursor-ew-resize"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            data-testid="before-after-slider"
          >
            {/* AFTER layer (base) */}
            <div className="bg-[#23201A] p-6 sm:p-10 lg:p-14">
              <div className="max-w-lg mx-auto">
                <div className="flex items-center gap-3 bg-[#2C2822] border border-[#B8933F]/25 px-4 py-3">
                  <Search size={15} className="text-[#B8933F]" />
                  <span className="text-sm text-[#F7F3EC]/85 truncate">{t.reveal.searchQuery}</span>
                </div>
                <div className="mt-5 bg-[#2C2822] border border-[#B8933F]/30 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-serif text-xl text-[#F7F3EC]">{t.reveal.afterName}</h3>
                      <p className="mt-1 text-xs text-[#B8933F] flex items-center gap-1.5">
                        <MapPin size={12} /> Dubai Marina
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-1" aria-hidden="true">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-[#B8933F]/35 to-[#9A9184]/20" />
                      ))}
                    </div>
                  </div>
                  <ul className="mt-4 space-y-2">
                    {t.reveal.afterTags.map((tag, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#F7F3EC]/80">
                        {[<Clock size={13} key="c" />, <Globe size={13} key="g" />, <Phone size={13} key="p" />][i]}
                        <span>{tag}</span>
                      </li>
                    ))}
                  </ul>
                  <span className="mt-5 inline-block bg-[#B8933F] text-[#191713] text-xs font-semibold tracking-[0.12em] uppercase px-4 py-2.5">
                    {t.reveal.afterCta}
                  </span>
                </div>
              </div>
            </div>

            {/* BEFORE layer (clipped) */}
            <div
              className="absolute inset-0 bg-[#141210] p-6 sm:p-10 lg:p-14"
              style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
              aria-hidden={pct < 8}
            >
              <div className="max-w-lg mx-auto opacity-80">
                <div className="flex items-center gap-3 bg-[#1E1B16]/80 border border-[#9A9184]/15 px-4 py-3">
                  <Search size={15} className="text-[#9A9184]/60" />
                  <span className="text-sm text-[#9A9184]/70 truncate">{t.reveal.searchQuery}</span>
                </div>
                <div className="mt-5 border border-dashed border-[#9A9184]/25 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="h-4 w-32 bg-[#9A9184]/20" />
                      <div className="mt-2 h-3 w-20 bg-[#9A9184]/10" />
                    </div>
                    <div className="w-16 h-16 sm:w-20 sm:h-20 border border-dashed border-[#9A9184]/25 flex items-center justify-center">
                      <ImageOff size={18} className="text-[#9A9184]/40" />
                    </div>
                  </div>
                  <ul className="mt-4 space-y-2">
                    {t.reveal.beforeItems.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-[#9A9184]/60">
                        <HelpCircle size={13} /> <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Handle */}
            <div className="absolute inset-y-0" style={{ left: `${pct}%` }}>
              <div className="absolute inset-y-0 -translate-x-1/2 w-px bg-[#B8933F] slider-handle-line" />
              <button
                data-testid="before-after-slider-handle"
                onKeyDown={onKeyDown}
                aria-label="Before / After slider"
                role="slider"
                aria-valuenow={Math.round(pct)}
                aria-valuemin={0}
                aria-valuemax={100}
                className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#191713] border-2 border-[#B8933F] flex items-center justify-center text-[#B8933F] cursor-ew-resize"
              >
                <span className="text-xs font-semibold">↔</span>
              </button>
            </div>

            {/* Labels */}
            <span className="absolute top-4 left-4 text-[0.6rem] tracking-[0.28em] uppercase text-[#9A9184] bg-[#191713]/70 px-2.5 py-1.5 pointer-events-none">{t.reveal.before}</span>
            <span className="absolute top-4 right-4 text-[0.6rem] tracking-[0.28em] uppercase text-[#B8933F] bg-[#191713]/70 px-2.5 py-1.5 pointer-events-none">{t.reveal.after}</span>
          </div>
          <p className="mt-4 text-xs text-[#9A9184]/70 italic" data-testid="reveal-disclaimer">{t.reveal.disclaimer}</p>
        </Reveal>
      </div>
    </section>
  );
};
